import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useMagicLinkTokensApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Button from '../../components/ui/Button';

const Container = styled.div``;

const ResendDialog = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;
`;

const ResendCard = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: 600px;
  width: 100%;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
`;

const TokenBox = styled.pre`
  background: #f5f7fa;
  border: 1px solid #e8ecf0;
  border-radius: 4px;
  padding: 12px;
  font-size: 13px;
  margin: 12px 0;
  white-space: pre-wrap;
  word-break: break-all;
`;

// #72 — yerel Btn kaldırıldı (tek kullanım noktası zaten $primary idi),
// paylaşılan Button'a bağlandı (bkz. t6-report.md).

export default function MagicLinkTokensPage() {
  const { t } = useTranslation();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const [revokeConfirm, setRevokeConfirm] = useState({ isOpen: false, id: null, purpose: '' });
  const [revoking, setRevoking] = useState(false);

  // The plaintext token is shown ONCE after a successful resend, then dropped.
  const [resendResult, setResendResult] = useState(null);
  const [resending, setResending] = useState(false);

  const { fetching, data, pagination, columns: baseColumns, revoke, resend } = useMagicLinkTokensApi(queryParams);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const handleRevokeClick = useCallback((row) => {
    setRevokeConfirm({ isOpen: true, id: row.id, purpose: row.purpose });
  }, []);

  const handleRevokeConfirm = useCallback(async () => {
    if (!revokeConfirm.id) return;
    setRevoking(true);
    await revoke(revokeConfirm.id, 'admin_action');
    setRevokeConfirm({ isOpen: false, id: null, purpose: '' });
    setRevoking(false);
  }, [revokeConfirm.id, revoke]);

  const handleResendClick = useCallback(async (row) => {
    setResending(true);
    const result = await resend(row.id);
    setResending(false);
    if (result?.plainToken) setResendResult(result);
  }, [resend]);

  const buildActions = useCallback((row) => {
    if (!row) return [];
    const actions = [];
    // Resend always available unless already revoked. Even consumed/expired
    // tokens can be resent — the new token replaces the old.
    if (!row.revokedAt) {
      actions.push({ type: 'edit', label: t('magicLinkToken.resend'), onClick: handleResendClick });
    }
    // Revoke only meaningful when active.
    if (row.derivedState === 'active') {
      actions.push({ type: 'divider' });
      actions.push({ type: 'delete', label: t('magicLinkToken.revoke'), onClick: handleRevokeClick });
    }
    return actions;
  }, [t, handleResendClick, handleRevokeClick]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          const actions = buildActions(row);
          if (actions.length === 0) return null;
          return <OptionsMenu actions={actions} row={row} />;
        },
      },
    },
  ], [baseColumns, data, buildActions]);

  return (
    <Container>
      <ConfirmDialog
        isOpen={revokeConfirm.isOpen}
        title={t('magicLinkToken.revokeTitle')}
        message={t('magicLinkToken.revokeMessage', { purpose: revokeConfirm.purpose })}
        onConfirm={handleRevokeConfirm}
        onCancel={() => setRevokeConfirm({ isOpen: false, id: null, purpose: '' })}
        loading={revoking}
      />

      <ResendDialog $open={!!resendResult}>
        {resendResult && (
          <ResendCard>
            <h3 style={{ margin: '0 0 16px' }}>{t('magicLinkToken.resendTitle')}</h3>
            <p>{t('magicLinkToken.resendNotice')}</p>
            <TokenBox>{resendResult.plainToken}</TokenBox>
            <p style={{ fontSize: 13, color: '#64748b' }}>
              {t('magicLinkToken.expiresAt')}: {resendResult.expiresAt}
            </p>
            <Button onClick={() => setResendResult(null)}>{t('common.close')}</Button>
          </ResendCard>
        )}
      </ResendDialog>

      {useMemo(() => (
        <DataTable
          title={t('magicLinkToken.title')}
          fetching={fetching || resending}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, resending, columns, data, pagination,
        filterList, handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
